import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClipboardCheck,
  faSpinner,
  faCheck,
  faExclamationTriangle,
  faBell,
  faArrowRight,
  faArrowLeft,
  faTriangleExclamation,
  faCalendarDay,
  faChartLine,
  faCircleCheck,
  faUserDoctor,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import mentalWellnessService, {
  ScreeningAssessment as ScreeningType,
  ScreeningQuestions,
} from '../../../services/mentalWellnessService';
import BackButton from '../../../components/BackButton/BackButton';
import './ScreeningAssessment.css';
import { useProfile } from '../../../context/ProfileContext';

/* ================================================================== */
/*  Types                                                              */
/* ================================================================== */

interface ToastState {
  message: string;
  type: 'success' | 'error' | 'info';
}

interface ScreeningConfig {
  id: string;
  name: string;
  fullName: string;
  description: string;
  color: string;
  maxScore: number;
  questionsKey: 'phq2' | 'phq9' | 'gad7';
}

/* ================================================================== */
/*  Screening Configurations                                           */
/* ================================================================== */

const SCREENING_TYPES: ScreeningConfig[] = [
  {
    id: 'depression',
    name: 'Depression Screening',
    fullName: 'Based on PHQ-9 · Starts with 2 quick questions',
    description: 'Starts with 2 quick questions. If needed, continues with 7 more for a complete assessment used by healthcare professionals worldwide.',
    color: '#7c3aed',
    maxScore: 27,
    questionsKey: 'phq9',
  },
  {
    id: 'anxiety',
    name: 'Anxiety Screening',
    fullName: 'Based on GAD-7 · 7 questions',
    description: '7 questions to understand your anxiety levels over the last 2 weeks. A standard clinical assessment.',
    color: '#10b981',
    maxScore: 21,
    questionsKey: 'gad7',
  },
];

const ANSWER_OPTIONS = [
  { value: 0, label: 'Not at all' },
  { value: 1, label: 'Several days' },
  { value: 2, label: 'More than half the days' },
  { value: 3, label: 'Nearly every day' },
];

const DEFAULT_QUESTIONS: Record<string, { number: number; text: string }[]> = {
  phq2: [
    { number: 1, text: 'Little interest or pleasure in doing things?' },
    { number: 2, text: 'Feeling down, depressed, or hopeless?' },
  ],
  phq9: [
    { number: 1, text: 'Little interest or pleasure in doing things?' },
    { number: 2, text: 'Feeling down, depressed, or hopeless?' },
    { number: 3, text: 'Trouble falling or staying asleep, or sleeping too much?' },
    { number: 4, text: 'Feeling tired or having little energy?' },
    { number: 5, text: 'Poor appetite or overeating?' },
    { number: 6, text: 'Feeling bad about yourself - or that you are a failure or have let yourself or your family down?' },
    { number: 7, text: 'Trouble concentrating on things, such as reading the newspaper or watching television?' },
    { number: 8, text: 'Moving or speaking so slowly that other people could have noticed? Or the opposite - being so fidgety or restless that you have been moving around a lot more than usual?' },
    { number: 9, text: 'Thoughts that you would be better off dead, or of hurting yourself in some way?' },
  ],
  gad7: [
    { number: 1, text: 'Feeling nervous, anxious, or on edge?' },
    { number: 2, text: 'Not being able to stop or control worrying?' },
    { number: 3, text: 'Worrying too much about different things?' },
    { number: 4, text: 'Trouble relaxing?' },
    { number: 5, text: 'Being so restless that it is hard to sit still?' },
    { number: 6, text: 'Becoming easily annoyed or irritable?' },
    { number: 7, text: 'Feeling afraid, as if something awful might happen?' },
  ],
};

/* ================================================================== */
/*  Helpers                                                            */
/* ================================================================== */

const formatDate = (iso: string): string => {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const getSeverityInfo = (type: string, score: number): { label: string; color: string; description: string } => {
  if (type === 'depression') {
    // Depression screening uses PHQ-9 scoring
    return getSeverityInfo('phq9', score);
  }
  if (type === 'anxiety') {
    return getSeverityInfo('gad7', score);
  }

  if (type === 'phq2') {
    return score >= 3
      ? { label: 'Positive Screen', color: '#f59e0b', description: 'Your score suggests you may benefit from a full PHQ-9 depression screening.' }
      : { label: 'Negative Screen', color: '#10b981', description: 'No significant indicators found. Continue monitoring your mood regularly.' };
  }

  if (type === 'phq9') {
    if (score <= 4) return { label: 'Minimal', color: '#10b981', description: 'Minimal depression symptoms. Continue healthy habits and self-care.' };
    if (score <= 9) return { label: 'Mild', color: '#84cc16', description: 'Mild depression symptoms. Consider lifestyle changes and monitoring.' };
    if (score <= 14) return { label: 'Moderate', color: '#f59e0b', description: 'Moderate depression symptoms. Consider speaking with a healthcare provider.' };
    if (score <= 19) return { label: 'Moderately Severe', color: '#f97316', description: 'Moderately severe symptoms. Professional support is recommended.' };
    return { label: 'Severe', color: '#ef4444', description: 'Severe depression symptoms. Please seek professional help promptly.' };
  }

  if (type === 'gad7') {
    if (score <= 4) return { label: 'Minimal', color: '#10b981', description: 'Minimal anxiety symptoms. Continue self-care practices.' };
    if (score <= 9) return { label: 'Mild', color: '#84cc16', description: 'Mild anxiety symptoms. Breathing exercises and mindfulness can help.' };
    if (score <= 14) return { label: 'Moderate', color: '#f59e0b', description: 'Moderate anxiety symptoms. Consider professional guidance.' };
    return { label: 'Severe', color: '#ef4444', description: 'Severe anxiety symptoms. Professional support is strongly recommended.' };
  }

  return { label: 'Unknown', color: '#94a3b8', description: '' };
};

/* ================================================================== */
/*  Toast Component                                                    */
/* ================================================================== */

const Toast: React.FC<{ message: string; type: string; onClose: () => void }> = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const iconMap: Record<string, IconDefinition> = { success: faCheck, error: faExclamationTriangle, info: faBell };

  return (
    <div className={`mw-sc-toast mw-sc-toast-${type}`}>
      <span className="mw-sc-toast-icon"><FontAwesomeIcon icon={iconMap[type] || faBell} /></span>
      <span className="mw-sc-toast-message">{message}</span>
      <button className="mw-sc-toast-close" onClick={onClose}>{'\u00D7'}</button>
    </div>
  );
};

/* ================================================================== */
/*  Main Component                                                     */
/* ================================================================== */

const ScreeningAssessment: React.FC = () => {
  const { t } = useTranslation(['common']);
  const { activeProfile } = useProfile();

  // Data
  const [history, setHistory] = useState<ScreeningType[]>([]);
  const [questions, setQuestions] = useState<Record<string, { number: number; text: string }[]>>(DEFAULT_QUESTIONS);
  const [loading, setLoading] = useState(true);

  // Assessment state
  const [selectedType, setSelectedType] = useState<ScreeningConfig | null>(null);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ScreeningType | null>(null);
  // Depression flow: start with 2 questions, extend to 9 if PHQ-2 score >= 3
  const [depressionPhase, setDepressionPhase] = useState<'initial' | 'extended' | 'done'>('initial');

  // UI
  const [toast, setToast] = useState<ToastState | null>(null);

  const showToast = useCallback((message: string, type: ToastState['type']) => {
    setToast({ message, type });
  }, []);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [historyData, questionsData] = await Promise.all([
        mentalWellnessService.getScreenings().catch(() => []),
        mentalWellnessService.getScreeningQuestions().catch(() => null),
      ]);
      setHistory(Array.isArray(historyData) ? historyData : []);
      if (questionsData) {
        const q: any = {};
        // API returns {phq2: {title, questions: [...]}, ...} — extract the questions array
        if (questionsData.phq2) q.phq2 = questionsData.phq2.questions || questionsData.phq2;
        if (questionsData.phq9) q.phq9 = questionsData.phq9.questions || questionsData.phq9;
        if (questionsData.gad7) q.gad7 = questionsData.gad7.questions || questionsData.gad7;
        if (Object.keys(q).length > 0) setQuestions({ ...DEFAULT_QUESTIONS, ...q });
      }
    } catch (err) {
      console.error('Failed to fetch screening data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, activeProfile]);

  // For depression screening: show only first 2 questions initially
  const isDepression = selectedType?.id === 'depression';
  const allQuestions = selectedType ? (questions[selectedType.questionsKey] || []) : [];
  const currentQuestions = isDepression && depressionPhase === 'initial'
    ? allQuestions.slice(0, 2)
    : allQuestions;
  const totalQuestions = currentQuestions.length;
  const allAnswered = totalQuestions > 0 && Object.keys(answers).length === totalQuestions;

  // Compute PHQ-2 score (first 2 questions) for depression flow
  const phq2Score = (answers['q1'] || 0) + (answers['q2'] || 0);

  // Handlers
  const handleSelectType = (config: ScreeningConfig) => {
    setSelectedType(config);
    setAnswers({});
    setCurrentQuestion(0);
    setResult(null);
    setDepressionPhase(config.id === 'depression' ? 'initial' : 'done');
  };

  const handleAnswer = (questionNum: number, value: number) => {
    setAnswers((prev) => ({ ...prev, [`q${questionNum}`]: value }));
  };

  const handleSubmit = async () => {
    if (!selectedType) return;

    // Depression flow: after first 2 questions, check if we need to extend
    if (isDepression && depressionPhase === 'initial' && allAnswered) {
      if (phq2Score >= 3) {
        // Extend to full PHQ-9 (show remaining 7 questions)
        setDepressionPhase('extended');
        setCurrentQuestion(2); // jump to question 3
        return;
      } else {
        // Low score — submit as PHQ-2 and show "no significant indicators"
        try {
          setSubmitting(true);
          const payload = {
            screening_type: 'phq2',
            date: new Date().toISOString().split('T')[0],
            answers,
          };
          const res = await mentalWellnessService.createScreening(payload);
          setResult(res);
          showToast('Screening completed!', 'success');
          await fetchData();
        } catch (err) {
          showToast('Failed to submit screening. Please try again.', 'error');
        } finally {
          setSubmitting(false);
        }
        return;
      }
    }

    // Full submission (extended PHQ-9 or GAD-7)
    if (!allAnswered) return;
    try {
      setSubmitting(true);
      const screeningType = isDepression ? 'phq9' : 'gad7';
      const payload = {
        screening_type: screeningType,
        date: new Date().toISOString().split('T')[0],
        answers,
      };
      const res = await mentalWellnessService.createScreening(payload);
      setResult(res);
      showToast('Screening completed!', 'success');
      await fetchData();
    } catch (err) {
      showToast('Failed to submit screening. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setSelectedType(null);
    setAnswers({});
    setCurrentQuestion(0);
    setResult(null);
    setDepressionPhase('initial');
  };

  /* ================================================================ */
  /*  Render: Type Selector                                            */
  /* ================================================================ */

  const renderTypeSelector = () => (
    <div className="mw-sc-type-grid">
      {SCREENING_TYPES.map((st, idx) => (
        <button
          key={st.id}
          className="mw-sc-type-card"
          style={{ borderTop: `4px solid ${st.color}`, animationDelay: `${idx * 0.08}s` }}
          onClick={() => handleSelectType(st)}
        >
          <div className="mw-sc-type-icon" style={{ background: `${st.color}12`, color: st.color }}>
            <FontAwesomeIcon icon={faClipboardCheck} />
          </div>
          <h4>{st.name}</h4>
          <span className="mw-sc-type-full">{st.fullName}</span>
          <p>{st.description}</p>
          <span className="mw-sc-type-btn" style={{ background: `linear-gradient(135deg, ${st.color}, ${st.color}cc)` }}>
            Start Assessment <FontAwesomeIcon icon={faArrowRight} />
          </span>
        </button>
      ))}
    </div>
  );

  /* ================================================================ */
  /*  Render: Questions                                                */
  /* ================================================================ */

  const renderQuestions = () => {
    if (!selectedType) return null;
    const question = currentQuestions[currentQuestion];
    if (!question) return null;
    const config = selectedType;

    return (
      <div className="mw-sc-assessment-card">
        <div className="mw-sc-assessment-header" style={{ background: `linear-gradient(135deg, ${config.color}12, ${config.color}05)` }}>
          <div className="mw-sc-assessment-header-top">
            <button className="mw-sc-back-to-types" onClick={handleReset}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back
            </button>
            <span className="mw-sc-type-badge" style={{ background: config.color, color: '#fff' }}>
              {config.name}
            </span>
          </div>
          <p className="mw-sc-assessment-intro">
            Over the <strong>last 2 weeks</strong>, how often have you been bothered by the following problems?
            {isDepression && depressionPhase === 'initial' && (
              <span style={{ display: 'block', marginTop: 6, fontSize: '0.85rem', opacity: 0.8 }}>
                Starting with 2 quick questions. We'll let you know if a deeper assessment is recommended.
              </span>
            )}
            {isDepression && depressionPhase === 'extended' && (
              <span style={{ display: 'block', marginTop: 6, fontSize: '0.85rem', opacity: 0.8 }}>
                Based on your initial answers, here are 7 more questions for a complete assessment.
              </span>
            )}
          </p>
          {/* Progress bar */}
          <div className="mw-sc-progress">
            <div className="mw-sc-progress-bar">
              <div
                className="mw-sc-progress-fill"
                style={{ width: `${((Object.keys(answers).length) / totalQuestions) * 100}%`, background: config.color }}
              />
            </div>
            <span className="mw-sc-progress-text">{Object.keys(answers).length}/{totalQuestions} answered</span>
          </div>
        </div>

        <div className="mw-sc-question-body">
          <div className="mw-sc-question-num">Question {question.number} of {totalQuestions}</div>
          <h3 className="mw-sc-question-text">{question.text}</h3>

          <div className="mw-sc-answer-grid">
            {ANSWER_OPTIONS.map((opt) => {
              const isSelected = answers[`q${question.number}`] === opt.value;
              return (
                <button
                  key={opt.value}
                  className={`mw-sc-answer-btn ${isSelected ? 'active' : ''}`}
                  style={isSelected ? { borderColor: config.color, background: `${config.color}10`, color: config.color } : {}}
                  onClick={() => handleAnswer(question.number, opt.value)}
                >
                  <span className="mw-sc-answer-value" style={isSelected ? { background: config.color, color: '#fff' } : {}}>
                    {opt.value}
                  </span>
                  <span className="mw-sc-answer-label">{opt.label}</span>
                </button>
              );
            })}
          </div>

          {/* Navigation */}
          <div className="mw-sc-question-nav">
            {currentQuestion > 0 && (
              <button
                className="mw-sc-nav-btn mw-sc-nav-prev"
                onClick={() => setCurrentQuestion((p) => p - 1)}
              >
                <FontAwesomeIcon icon={faArrowLeft} /> Previous
              </button>
            )}
            <div style={{ flex: 1 }} />
            {currentQuestion < totalQuestions - 1 ? (
              <button
                className="mw-sc-nav-btn mw-sc-nav-next"
                style={{ background: `linear-gradient(135deg, ${config.color}, ${config.color}cc)` }}
                onClick={() => setCurrentQuestion((p) => p + 1)}
                disabled={answers[`q${question.number}`] === undefined}
              >
                Next <FontAwesomeIcon icon={faArrowRight} />
              </button>
            ) : (
              <button
                className="mw-sc-nav-btn mw-sc-nav-submit"
                style={{ background: `linear-gradient(135deg, ${config.color}, ${config.color}cc)` }}
                onClick={handleSubmit}
                disabled={!allAnswered || submitting}
              >
                {submitting ? (
                  <><FontAwesomeIcon icon={faSpinner} spin /> Submitting...</>
                ) : isDepression && depressionPhase === 'initial' ? (
                  <><FontAwesomeIcon icon={faCircleCheck} /> Check Results</>
                ) : (
                  <><FontAwesomeIcon icon={faCircleCheck} /> Submit</>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ================================================================ */
  /*  Render: Results                                                  */
  /* ================================================================ */

  const renderResults = () => {
    if (!result || !selectedType) return null;
    const totalScore = result.total_score ?? Object.values(answers).reduce((a, b) => a + b, 0);
    const severity = getSeverityInfo(selectedType.id, totalScore);
    const isDepressionType = selectedType.id === 'depression' || selectedType.id === 'phq9';
    const isAnxietyType = selectedType.id === 'anxiety' || selectedType.id === 'gad7';
    const isFlagged = result.flagged || totalScore >= (isDepressionType ? 15 : isAnxietyType ? 10 : 3);

    return (
      <div className="mw-sc-results-card">
        <div className="mw-sc-results-header">
          <FontAwesomeIcon icon={faCircleCheck} style={{ color: severity.color }} />
          <h3>Assessment Complete</h3>
        </div>

        {/* Score bar */}
        <div className="mw-sc-score-section">
          <div className="mw-sc-score-big" style={{ color: severity.color }}>{totalScore}</div>
          <div className="mw-sc-score-max">/ {selectedType.maxScore}</div>
        </div>

        <div className="mw-sc-score-bar-wrap">
          <div className="mw-sc-score-bar">
            <div
              className="mw-sc-score-bar-fill"
              style={{ width: `${(totalScore / selectedType.maxScore) * 100}%`, background: severity.color }}
            />
          </div>
        </div>

        <span className="mw-sc-severity-badge" style={{ background: `${severity.color}15`, color: severity.color, borderColor: `${severity.color}30` }}>
          {severity.label}
        </span>

        <p className="mw-sc-severity-desc">{severity.description}</p>

        {isFlagged && (
          <div className="mw-sc-flag-alert">
            <FontAwesomeIcon icon={faUserDoctor} />
            <span>We recommend speaking with a healthcare professional about your results.</span>
          </div>
        )}

        <div className="mw-sc-results-actions">
          <button className="mw-sc-btn mw-sc-btn-secondary" onClick={handleReset}>
            Take Another Assessment
          </button>
        </div>
      </div>
    );
  };

  /* ================================================================ */
  /*  Render: History                                                  */
  /* ================================================================ */

  const renderHistory = () => {
    if (history.length === 0) return null;

    const sorted = [...history].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    return (
      <div className="mw-sc-history-card">
        <h3 className="mw-sc-history-title">
          <FontAwesomeIcon icon={faChartLine} /> Past Screenings
        </h3>
        <div className="mw-sc-history-list">
          {sorted.slice(0, 10).map((s) => {
            const historyLabels: Record<string, { label: string; color: string }> = {
              phq2: { label: 'Depression (Quick)', color: '#7c3aed' },
              phq9: { label: 'Depression (Full)', color: '#7c3aed' },
              gad7: { label: 'Anxiety', color: '#10b981' },
            };
            const historyInfo = historyLabels[s.screening_type] || { label: s.screening_type.toUpperCase(), color: '#64748b' };
            const severity = getSeverityInfo(s.screening_type, s.total_score || 0);
            return (
              <div className="mw-sc-history-item" key={s.id}>
                <div className="mw-sc-history-left">
                  <span className="mw-sc-history-type" style={{ color: historyInfo.color }}>
                    {historyInfo.label}
                  </span>
                  <span className="mw-sc-history-date">{formatDate(s.date)}</span>
                </div>
                <div className="mw-sc-history-right">
                  <span className="mw-sc-history-score">{s.total_score}</span>
                  <span
                    className="mw-sc-history-severity"
                    style={{ background: `${severity.color}15`, color: severity.color, borderColor: `${severity.color}30` }}
                  >
                    {severity.label}
                  </span>
                  {s.flagged && (
                    <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#ef4444', fontSize: '0.8rem' }} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /* ================================================================ */
  /*  Main Render                                                      */
  /* ================================================================ */

  return (
    <div className="mw-sc-page">
      {toast && (
        <div className="mw-sc-toast-container">
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        </div>
      )}

      {/* Hero */}
      <section className="mw-sc-hero">
        <div className="mw-sc-hero-circle mw-sc-hero-circle-1" />
        <div className="mw-sc-hero-circle mw-sc-hero-circle-2" />
        <Container>
          <BackButton />
          <div className="mw-sc-hero-content">
            <h1 className="mw-sc-hero-title">Mental Health Screening</h1>
            <p className="mw-sc-hero-subtitle">
              Take evidence-based screenings to check in on your mental health. Your results are private and confidential.
            </p>
          </div>
        </Container>
      </section>

      <Container>
        <div className="mw-sc-content">
          {result ? (
            renderResults()
          ) : selectedType ? (
            renderQuestions()
          ) : (
            <>
              {renderTypeSelector()}
              {!loading && renderHistory()}
            </>
          )}
        </div>
      </Container>
    </div>
  );
};

export default ScreeningAssessment;
